import { useState } from 'react';
import { Megaphone, Check } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import { SECTION_COLOR, SECTION_BG, hlavicky } from '../komunikace';
import PrilohyVyber from '../prilohy/PrilohyVyber';
import PrilohyMrizka from '../prilohy/PrilohyMrizka';

/**
 * Nové / upravované oznámení — `ModalShell` (norma: založení = modal).
 * Komu se u zveřejněného oznámení už nemění (příjemci jsou zapsaní), jde jen
 * upravit text, platnost a připnutí.
 */
export default function OznameniEditor({ isDark, pozice = [], puvodni = null, onClose, onUlozeno }) {
    const [nazev, setNazev] = useState(puvodni?.nazev || '');
    const [text, setText] = useState(puvodni?.text || '');
    const [cilTyp, setCilTyp] = useState(puvodni?.cil?.typ || 'vsichni');
    const [vybranePozice, setVybranePozice] = useState(puvodni?.cil?.pozice || []);
    const [potvrzeni, setPotvrzeni] = useState(puvodni ? !!puvodni.vyzaduje_potvrzeni : true);
    const [pripnout, setPripnout] = useState(!!puvodni?.pripnuto);
    const [platiDo, setPlatiDo] = useState(puvodni?.plati_do || '');
    const [ukladam, setUkladam] = useState(false);
    // Nově přiložené (nahrané, nepřipojené) a u úpravy ty, které autor odebral.
    const [prilohy, setPrilohy] = useState([]);
    const [puvodniPrilohy, setPuvodniPrilohy] = useState(puvodni?.prilohy || []);
    const [nahravam, setNahravam] = useState(false);
    const [chyba, setChyba] = useState(null);

    const lze = nazev.trim() && text.trim() && (puvodni || cilTyp === 'vsichni' || vybranePozice.length > 0) && !ukladam && !nahravam;

    const ulozit = async () => {
        if (!lze) return;
        setUkladam(true);
        setChyba(null);
        const telo = {
            nazev: nazev.trim(), text: text.trim(),
            vyzaduje_potvrzeni: potvrzeni, pripnuto: pripnout, plati_do: platiDo || null,
            prilohy: prilohy.map(p => p.id),
            ...(puvodni ? { odebrat_prilohy: (puvodni.prilohy || []).filter(p => !puvodniPrilohy.some(x => x.id === p.id)).map(p => p.id) } : {}),
            ...(puvodni ? {} : { cil: cilTyp === 'vsichni' ? { typ: 'vsichni' } : { typ: 'pozice', pozice: vybranePozice } }),
        };
        try {
            const res = await fetch(puvodni ? `/api/nastenka/${puvodni.id}` : '/api/nastenka', {
                method: puvodni ? 'PATCH' : 'POST', headers: hlavicky(), body: JSON.stringify(telo),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(data.error || data.message || 'Nepodařilo se uložit.'); return; }
            onUlozeno(data);
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
        } finally {
            setUkladam(false);
        }
    };

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const pole = { width: '100%', borderRadius: 8, padding: '0 12px', outline: 'none', fontSize: 14, height: 40, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light' };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '16px 0 6px' };
    const volba = (on) => ({
        height: 36, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        background: on ? SECTION_BG : 'transparent', border: `1px solid ${on ? SECTION_COLOR : hrana}`, color: on ? SECTION_COLOR : 'var(--text-secondary)',
        display: 'inline-flex', alignItems: 'center', gap: 6,
    });
    const Prepinac = ({ zapnuto, onClick, titulek, popis }) => (
        <button type="button" onClick={onClick} className="w-full flex items-center text-left"
            style={{ gap: 10, minHeight: 52, padding: '8px 12px', borderRadius: 8, border: `1px solid ${hrana}`, background: 'transparent', cursor: 'pointer', marginTop: 8 }}>
            <span style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${zapnuto ? SECTION_COLOR : hrana}`, background: zapnuto ? SECTION_COLOR : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {zapnuto && <Check size={14} color="#fff" />}
            </span>
            <span className="flex-1">
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{titulek}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{popis}</span>
            </span>
        </button>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={puvodni ? 'Upravit oznámení' : 'Nové oznámení'} ikona={Megaphone} barva={SECTION_COLOR} sirka={560}
            paticka={(
                <div className="flex items-center gap-2">
                    {chyba && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
                    <button type="button" onClick={ulozit} disabled={!lze} className="rounded-lg font-semibold ml-auto"
                        style={{ height: 42, padding: '0 18px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: lze ? 'pointer' : 'not-allowed', opacity: lze ? 1 : 0.45 }}>
                        {ukladam ? 'Ukládám…' : puvodni ? 'Uložit' : 'Zveřejnit'}
                    </button>
                </div>
            )}>
            <div style={{ padding: '0 16px 16px' }}>
                <p style={popisek}>Nadpis</p>
                <input value={nazev} onChange={e => setNazev(e.target.value)} maxLength={150} placeholder="Např. Nový rozpis směn od 15. 10." style={pole} autoFocus />

                <p style={popisek}>Text</p>
                <textarea value={text} onChange={e => setText(e.target.value)} rows={7}
                    placeholder={'Co se mění a od kdy. Odstavce oddělte prázdným řádkem.\n- odrážky začínají pomlčkou\n**tučně** pro důležité'}
                    style={{ ...pole, height: 'auto', padding: '10px 12px', lineHeight: 1.5, resize: 'vertical' }} />

                {puvodniPrilohy.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <PrilohyMrizka prilohy={puvodniPrilohy} isDark={isDark} velikostNahledu={72}
                            onOdebrat={(p) => setPuvodniPrilohy(prev => prev.filter(x => x.id !== p.id))} />
                    </div>
                )}
                <PrilohyVyber typ="oznameni" prilohy={prilohy} onChange={setPrilohy} onNahravani={setNahravam} isDark={isDark}
                    max={10 - puvodniPrilohy.length} />

                {!puvodni && (
                    <>
                        <p style={popisek}>Komu</p>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" style={volba(cilTyp === 'vsichni')} onClick={() => setCilTyp('vsichni')}>Všichni</button>
                            <button type="button" style={volba(cilTyp === 'pozice')} onClick={() => setCilTyp('pozice')}>Podle pozice</button>
                        </div>
                        {cilTyp === 'pozice' && (
                            <div className="flex flex-wrap gap-2" style={{ marginTop: 8 }}>
                                {pozice.map(p => {
                                    const on = vybranePozice.includes(p.id);
                                    return (
                                        <button key={p.id} type="button" style={volba(on)}
                                            onClick={() => setVybranePozice(prev => (on ? prev.filter(x => x !== p.id) : [...prev, p.id]))}>
                                            {on && <Check size={13} />} {p.nazev}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 6 }}>
                            Vedoucí posílá jen svým podřízeným; vedení komukoli.
                        </p>
                    </>
                )}

                <Prepinac zapnuto={potvrzeni} onClick={() => setPotvrzeni(v => !v)} titulek="Vyžaduje „Přečetl jsem“" popis="Uvidíte, kdo potvrdil, a nepotvrzeným můžete připomenout." />
                <Prepinac zapnuto={pripnout} onClick={() => setPripnout(v => !v)} titulek="Připnout nahoru" popis="Zůstane první na Nástěnce, dokud platí." />

                <p style={popisek}>Platí do (nepovinné)</p>
                <input type="date" value={platiDo} onChange={e => setPlatiDo(e.target.value)} style={{ ...pole, maxWidth: 220 }} />
            </div>
        </ModalShell>
    );
}
